import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { CreateIncidentResponseData } from '../../Types/Incident';
import { formatDateTime } from '../../Utils/dateHelpers';

export type CreateIncidentProgressState =
  | 'idle'
  | 'creating'
  | 'loadingRoster'
  | 'finalizing'
  | 'success'
  | 'error';

interface CreateIncidentProgressDialogProps {
  open: boolean;
  state: CreateIncidentProgressState;
  errorMessage?: string | null;
  incidentName?: string;
  createdIncident?: CreateIncidentResponseData | null;
  onOk: () => void;
  onClose: () => void;
  onRetry?: () => void;
}

const STEPS = [
  { key: 'creating' as const, label: 'Creating incident' },
  { key: 'loadingRoster' as const, label: 'Loading roster' },
  { key: 'finalizing' as const, label: 'Finalizing setup' },
];

const STEP_ORDER: Record<string, number> = { creating: 0, loadingRoster: 1, finalizing: 2, success: 3 };

const DESCRIPTIONS: Record<string, string> = {
  creating: 'Creating the incident record and preparing the evacuation workflow.',
  loadingRoster: 'Loading assigned employees, badge activity, and roster context for this incident.',
  finalizing: 'Finalizing counts and preparing the roster views.',
};

const isPending = (state: CreateIncidentProgressState): boolean =>
  state === 'creating' || state === 'loadingRoster' || state === 'finalizing';

/* ── Composition grid row ── */
interface CompositionRow { label: string; count: number }

const getCompositionRows = (counts: CreateIncidentResponseData['rosterCounts']): CompositionRow[] => {
  if (!counts) return [];
  const rows: CompositionRow[] = [
    { label: 'Employees', count: counts.employees },
    { label: 'Contractors', count: counts.contractors },
    { label: 'Visitors', count: counts.visitors },
    { label: 'Tenants', count: counts.tenants },
  ];
  // Only show non-zero rows
  return rows.filter((r) => r.count > 0);
};

/* ── Success content ── */
const SuccessContent: React.FC<{
  incidentName?: string;
  createdIncident?: CreateIncidentResponseData | null;
}> = ({ incidentName, createdIncident }) => {
  const name = createdIncident?.incidentName ?? incidentName ?? 'Incident';
  const location = createdIncident?.location;
  const startedAt = createdIncident?.startedAtUtc;
  const rosterCounts = createdIncident?.rosterCounts;
  const total = rosterCounts?.total ?? 0;
  const hasRoster = rosterCounts != null;
  const isZeroRoster = hasRoster && total === 0;
  const compositionRows = getCompositionRows(rosterCounts);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      {/* Incident header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CheckIcon sx={{ fontSize: 40, color: appColors.status.success, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: appColors.text.heading, lineHeight: 1.3 }}>
            {name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
            <Chip label="Active" size="small" color="success" sx={{ fontWeight: 600, height: 22 }} />
            {location && (
              <Typography variant="body2" sx={{ color: appColors.text.body }}>
                {location}
              </Typography>
            )}
            {startedAt && (
              <Typography variant="body2" sx={{ color: appColors.text.caption }}>
                {formatDateTime(startedAt)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Setup checklist */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.25 }}>
          Setup Complete
        </Typography>
        <ChecklistItem label="Incident created" checked />
        {isZeroRoster ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ fontSize: 20, color: appColors.status.warning }} />
            <Typography variant="body2" sx={{ color: appColors.status.warning, fontWeight: 500 }}>
              Roster loaded: 0 people
            </Typography>
          </Box>
        ) : (
          <ChecklistItem
            label={hasRoster ? `Roster loaded: ${total} ${total === 1 ? 'person' : 'people'}` : 'Roster loaded'}
            checked
          />
        )}
        <ChecklistItem label="Ready to begin headcount" checked />
      </Box>

      {/* Roster summary */}
      {hasRoster && total > 0 && (
        <>
          <Divider />
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: appColors.text.heading }}>
                Roster Summary
              </Typography>
              <Typography variant="body2" sx={{ color: appColors.text.caption }}>
                All {total} marked <strong>Not Checked</strong>
              </Typography>
            </Box>

            {/* Composition grid */}
            {compositionRows.length > 0 && (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 0.5,
                bgcolor: appColors.surface.card,
                borderRadius: '8px',
                p: 1.5,
                border: `1px solid ${appColors.border.default}`,
              }}>
                {compositionRows.map((row) => (
                  <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5 }}>
                    <Typography variant="body2" sx={{ color: appColors.text.body }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading }}>
                      {row.count}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Roster count unavailable */}
      {!hasRoster && (
        <>
          <Divider />
          <Typography variant="body2" sx={{ color: appColors.text.caption, fontStyle: 'italic' }}>
            Roster count unavailable — counts will appear on the roster views page.
          </Typography>
        </>
      )}

      {/* Zero roster warning */}
      {isZeroRoster && (
        <Alert severity="warning" sx={{ borderRadius: '8px' }}>
          No people were loaded into the roster. You can still proceed and add people manually from the roster views.
        </Alert>
      )}
    </Box>
  );
};

/* ── Checklist item ── */
const ChecklistItem: React.FC<{ label: string; checked: boolean }> = ({ label, checked }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {checked && <CheckIcon sx={{ fontSize: 20, color: appColors.status.success }} />}
    <Typography variant="body2" sx={{ color: appColors.text.body }}>
      {label}
    </Typography>
  </Box>
);

const CreateIncidentProgressDialog: React.FC<CreateIncidentProgressDialogProps> = ({
  open,
  state,
  errorMessage,
  incidentName,
  createdIncident,
  onOk,
  onClose,
  onRetry,
}) => {
  const pending = isPending(state);
  const currentStepIndex = STEP_ORDER[state] ?? -1;

  const title = state === 'success'
    ? 'Incident Created'
    : state === 'error'
      ? 'Unable to create incident'
      : STEPS.find((s) => s.key === state)?.label ?? 'Creating incident';

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={(_e, reason) => {
        if (pending) return;
        if (reason === 'backdropClick' && pending) return;
        if (state === 'error') onClose();
      }}
      aria-labelledby="create-incident-progress-title"
    >
      <DialogTitle
        id="create-incident-progress-title"
        sx={{ fontWeight: 700, color: appColors.text.heading, pb: 1 }}
      >
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Success state */}
        {state === 'success' && (
          <SuccessContent incidentName={incidentName} createdIncident={createdIncident} />
        )}

        {/* Error state */}
        {state === 'error' && (
          <Box sx={{ py: 1 }}>
            <Alert severity="error" sx={{ mb: 1 }}>
              {errorMessage || 'Something went wrong while creating the incident. Please try again.'}
            </Alert>
          </Box>
        )}

        {/* Pending states */}
        {pending && (
          <Box sx={{ py: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: appColors.text.body, mb: 2.5 }}
              aria-live="polite"
            >
              {DESCRIPTIONS[state] ?? ''}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {STEPS.map((step, idx) => {
                const isComplete = idx < currentStepIndex;
                const isActive = step.key === state;
                const isFuture = idx > currentStepIndex;

                return (
                  <Box
                    key={step.key}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    {/* Icon */}
                    {isComplete && (
                      <CheckIcon sx={{ fontSize: 22, color: appColors.status.success }} />
                    )}
                    {isActive && (
                      <CircularProgress size={20} thickness={5} sx={{ color: appColors.primary.main }} />
                    )}
                    {isFuture && (
                      <Box sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: `2px solid ${appColors.border.default}`,
                        flexShrink: 0,
                      }} />
                    )}

                    {/* Label */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 400,
                        color: isFuture
                          ? appColors.text.placeholder
                          : isComplete
                            ? appColors.text.body
                            : appColors.text.heading,
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {state === 'success' && (
          <Button
            variant="contained"
            onClick={onOk}
            endIcon={<ArrowForwardIcon />}
            sx={{ fontWeight: 700 }}
          >
            Continue to Roster Views
          </Button>
        )}
        {state === 'error' && (
          <>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            {onRetry && (
              <Button variant="contained" onClick={onRetry} sx={{ fontWeight: 700 }}>
                Try Again
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateIncidentProgressDialog;
